import type { MessageActionInvocationStatus } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	type CommandConstructor,
	type CommandProperties,
	type SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

const JSM_CREATE_KB_SYNC_COMMANDS = {
	CHAT_TO_STAGING_MESSAGE: 'jsm-create-kb-chat-to-staging-message-command',
	STAGING_TO_CHAT_MESSAGE: 'jsm-create-kb-staging-to-chat-message-command',
	CREATE_ITEMS: 'jsm-create-kb-create-items-command',
	CARD_EVENT: 'jsm-kb-card-event-command',
	CREATE_ARTICLES: 'jsm-create-kb-create-articles-command',
} as const;

/**
 * Rovo chat → JSM create-KB embed: push a message into the staging iframe UI.
 */
export interface JsmCreateKbChatToStagingMessageCommandProperties extends CommandProperties {
	message: string;
}

export const JsmCreateKbChatToStagingMessageCommand: CommandConstructor<
	JsmCreateKbChatToStagingMessageCommandProperties,
	void
> = createCommand<JsmCreateKbChatToStagingMessageCommandProperties, void>(
	JSM_CREATE_KB_SYNC_COMMANDS.CHAT_TO_STAGING_MESSAGE,
);

/**
 * JSM create-KB embed → Rovo chat: push a message back to the in-chat Create KB card.
 */
export interface JsmCreateKbStagingToChatMessageCommandProperties extends CommandProperties {
	message: string;
}

export const JsmCreateKbStagingToChatMessageCommand: CommandConstructor<
	JsmCreateKbStagingToChatMessageCommandProperties,
	void
> = createCommand<JsmCreateKbStagingToChatMessageCommandProperties, void>(
	JSM_CREATE_KB_SYNC_COMMANDS.STAGING_TO_CHAT_MESSAGE,
);

/** Same item payload shape as {@link JiraCreateItemsCommand}; keep bridge types free of smart-creation. */
export interface JsmCreateKbCreateItemsCommandProperties extends CommandProperties {
	actions: JSMKBCreateItemsAction[];
	type: 'ARTICLES' | 'TOPICS';
}

export interface JSMKbCardEventCommandProperties extends CommandProperties {
	type: string;
	data: Record<string, SerializableValue>;
}

export interface JSMKBStagingAreaEventCommandProperties extends CommandProperties {
	type: string;
	data: Record<string, SerializableValue>;
}

export type KBArticleData = {
	title: string;
	summary: string;
	body: SerializableValue;
	articleId: string;
	rovoTopicId: string;
};

/** Expected data from BE for created KB article item */
export type KBCreateItemsData = {
	articleIds?: string[];
	articles?: KBArticleData[];
	projectId: number;
};

export type JSMKBCreateItemsAction = {
	/**
	 * The unique key identifier for the items creation request.
	 */
	key: string;
	/**
	 * The unique identifier for the action.
	 */
	actionId?: string;
	/**
	 * The unique identifier for the invocation.
	 */
	invocationId: string;
	/**
	 * The current status of the creation process.
	 */
	status?: MessageActionInvocationStatus;
	/**
	 * The data payload containing the content for the new item.
	 */
	data: KBCreateItemsData;
};

export const JsmCreateKbCreateItemsCommand: CommandConstructor<
	JsmCreateKbCreateItemsCommandProperties,
	SerializableValue
> = createCommand<JsmCreateKbCreateItemsCommandProperties>(
	JSM_CREATE_KB_SYNC_COMMANDS.CREATE_ITEMS,
);

export const JSMKbCardEventCommand: CommandConstructor<JSMKbCardEventCommandProperties, void> =
	createCommand<JSMKbCardEventCommandProperties, void>(JSM_CREATE_KB_SYNC_COMMANDS.CARD_EVENT);

export const JsmCreateKbArticlesCommand: CommandConstructor<
	JsmCreateKbCreateItemsCommandProperties,
	SerializableValue
> = createCommand<JsmCreateKbCreateItemsCommandProperties>(
	JSM_CREATE_KB_SYNC_COMMANDS.CREATE_ARTICLES,
);

export const JSMKBStagingAreaEventCommand: CommandConstructor<
	JSMKBStagingAreaEventCommandProperties,
	void
> = createCommand<JSMKBStagingAreaEventCommandProperties, void>(
	'jsm-kb-staging-area-event-command',
);
